import { Controller } from "@hotwired/stimulus";

// Generic drag-and-drop row reordering. Pair with a row that has a
// data-drag-reorder-target="row" wrapper and a draggable handle cell
// (see shared/_drag_reorder_table.html.erb). Dropping moves the whole row,
// reindexes every row 0..n, updates each row's [data-position-label], and
// dispatches a `change` event on each row's hidden index input so its
// existing rails-ujs data-remote/PUT wiring persists the new order without
// a page refresh.
export default class extends Controller {
  static targets = ["row"];

  connect() {
    this.draggedRow = null;
    this.indicatorRow = null;
  }

  dragStart(e) {
    this.draggedRow = e.target.closest("[data-drag-reorder-target~='row']");
    if (!this.draggedRow) return;

    this.draggedRow.classList.add("opacity-50");
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", "");
  }

  dragEnd() {
    if (this.draggedRow) this.draggedRow.classList.remove("opacity-50");
    this.draggedRow = null;
    this.clearIndicator();
  }

  dragEnter(e) {
    e.preventDefault();
  }

  dragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";

    const targetRow = e.target.closest("[data-drag-reorder-target~='row']");
    if (!targetRow || targetRow === this.draggedRow) {
      this.clearIndicator();
      return;
    }

    this.showIndicator(targetRow, this.insertsAfter(e, targetRow));
  }

  drop(e) {
    e.preventDefault();
    this.clearIndicator();
    if (!this.draggedRow) return;

    const targetRow = e.target.closest("[data-drag-reorder-target~='row']");
    if (!targetRow || targetRow === this.draggedRow) return;

    const insertAfter = this.insertsAfter(e, targetRow);
    targetRow.parentNode.insertBefore(this.draggedRow, insertAfter ? targetRow.nextSibling : targetRow);

    this.reindex();
  }

  insertsAfter(e, row) {
    const rect = row.getBoundingClientRect();
    return (e.clientY - rect.top) > (rect.height / 2);
  }

  // Shows a colored border on the edge of `row` (top or bottom) where the
  // dragged row would land if dropped right now.
  showIndicator(row, insertAfter) {
    if (this.indicatorRow === row && this.indicatorInsertAfter === insertAfter) return;

    this.clearIndicator();
    row.classList.add(insertAfter ? "border-b-4" : "border-t-4", "border-blue-500");
    this.indicatorRow = row;
    this.indicatorInsertAfter = insertAfter;
  }

  clearIndicator() {
    if (!this.indicatorRow) return;

    this.indicatorRow.classList.remove("border-t-4", "border-b-4", "border-blue-500");
    this.indicatorRow = null;
    this.indicatorInsertAfter = null;
  }

  reindex() {
    this.rowTargets.forEach((row, i) => {
      const label = row.querySelector("[data-position-label]");
      if (label) label.textContent = i + 1;

      const input = row.querySelector("input[type=hidden][data-remote]");
      if (input && Number(input.value) !== i) {
        input.value = i;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });
  }
}
